import { FlightPurposeField } from '../components/flights/FlightPurpose';
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import {
  Backdrop,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";

import FlightOutlinedIcon from '@mui/icons-material/FlightOutlined';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import {
  Page,
  InfoTable,
  FuelChecksTable,
  WaypointsTable,
  TimeTable,
  FuelTable,
  FilesList,
  Map,
  DeleteFlightModal,
  JourneyLogModal,
  FlightLeonTransfer,
  FlightSendFilesOverlay,
  WidePageContainer,
} from "../components";
import { CockpitCard, PageHeader } from "../components/design";
import {
  FlightAuditTrail,
  FlightFuelCheckSummaryCard,
  FlightVerdictChips,
} from "../components/flights/FlightVerdict";
import { useFlightLeonRetransfer, useGetFlight } from "../hooks/useFlight";
import useAuthSnapshot from "../hooks/useAuthSnapshot";
import FlightJourneyLog from '../components/journey-log/FlightJourneyLog';

const Flight: FunctionComponent = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [activeMarker, setActiveMarker] = useState("");
  const [isJLModalOpen, setIsJLModalOpen] = useState(false);
  const [isSendOverlayOpen, setIsSendOverlayOpen] = useState(false);
  const [isLeonRetransferDialogOpen, setIsLeonRetransferDialogOpen] = useState(false);
  const [leonRetransferError, setLeonRetransferError] = useState("");
  const [deleteID, setDeleteId] = useState('');
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
  const { data, isLoading, isSuccess, isError, refetch } = useGetFlight(id || "");
  const leonRetransferMutation = useFlightLeonRetransfer();
  const { isAcmiClient, isSuperAdmin, isCompanyAdmin } = useAuthSnapshot();
  const {
    waypoints,
    gpsTrack,
    recorded_gps_track,
    routePoints,
    supplementary,
    time,
    weight,
    altn,
    fuel,
    fuelRows,
    fueling,
    approach,
    deicing,
    delay,
    crewInfo,
    leonIntegration,
    leonTransfer,
    weightUnit,
    verdicts,
    auditTrail,
    fuelChecksSummary,
    signatureDisplay,
  } = data || {};
  const fuelChecks = waypoints?.filter(({ isAfqSet }: Waypoint) => !!isAfqSet);
  const signatureVerdict = (verdicts || []).find(
    (verdict: FlightVerdict) => verdict.id === "signature",
  );
  const signatureMode =
    signatureDisplay?.mode ||
    (signatureVerdict?.detail &&
    typeof signatureVerdict.detail === "object" &&
    !Array.isArray(signatureVerdict.detail)
      ? (signatureVerdict.detail as { mode?: string }).mode
      : null) ||
    "legacy";
  const signaturePrimaryLabel =
    signatureDisplay?.primaryLabel ||
    (signatureMode === "legacy"
      ? supplementary?.user_tlc
        ? `Signed by ${supplementary.user_tlc}`
        : "Unsigned"
      : supplementary?.user_tlc
        ? `Uploaded from device of ${supplementary.user_tlc}`
        : null);
  const signatureSecondaryLabel =
    signatureDisplay?.secondaryLabel ||
    (signatureMode === "truthful" ? "No crew attestation" : null);
  const signatureChipLabel =
    signatureDisplay?.chipLabel ||
    (signatureMode === "legacy"
      ? supplementary?.user_tlc
        ? `TLC ${supplementary.user_tlc}`
        : "Unsigned"
      : signaturePrimaryLabel || signatureSecondaryLabel || "No crew attestation");
  const componentRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Flight ${id} Details`,
    pageStyle: "padding: 20px",
  });

  const handleOpenDeleteModal = () => {
    setDeleteId(id || '');
  }

  const handleCloseDeleteModal = () => setDeleteId('');

  const handleToggleJLModal = () => {
    setIsJLModalOpen(!isJLModalOpen);
  }

  const handleActiveMarker = (marker: string) => {
    if (marker === activeMarker) {
      return;
    }
    setActiveMarker(marker);
  };

  const handleOpenSendOverlay = () => {
    setIsSendOverlayOpen(true);
  };

  const handleCloseSendOverlay = () => {
    setIsSendOverlayOpen(false);
  };

  const handleOpenLeonRetransferDialog = () => {
    setLeonRetransferError("");
    setIsLeonRetransferDialogOpen(true);
  };

  const handleCloseLeonRetransferDialog = () => {
    if (leonRetransferMutation.isLoading) {
      return;
    }
    setIsLeonRetransferDialogOpen(false);
    setLeonRetransferError("");
  };

  const handleConfirmLeonRetransfer = async () => {
    if (!id) {
      return;
    }

    setLeonRetransferError("");
    try {
      await leonRetransferMutation.mutateAsync(id);
      await refetch();
      setIsLeonRetransferDialogOpen(false);
    } catch (error) {
      setLeonRetransferError(
        error instanceof Error ? error.message : "Unable to request LEON re-transfer",
      );
    }
  };

  const showLeonIdInfo =
    (isSuperAdmin || isCompanyAdmin) &&
    Boolean(leonIntegration?.enabled && leonIntegration?.operatorCode);

  useEffect(() => {
    const action = searchParams.get("action");
    if (action === "journey-log") {
      setIsJLModalOpen(true);
    } else if (action === "send-files" && !isAcmiClient) {
      setIsSendOverlayOpen(true);
    }
  }, [isAcmiClient, searchParams]);

  return (
    <>
      <WidePageContainer sx={{ py: { xs: 2, md: 3 }, minWidth: 0, overflowX: "hidden" }}>
        <Backdrop sx={{ zIndex: 1 }} open={isLoading}>
          <CircularProgress />
        </Backdrop>
        <Page style={{ paddingTop: 0 }}>
          {isError && (
            <Alert severity="error">Flight {id} not found</Alert>
          )}
          {isSuccess && (
            <Stack spacing={2.5} className="cockpit-ui" ref={componentRef}>
              <PageHeader
                eyebrow={<><RouterLink to="/flights" style={{ color: "inherit" }}>Flights</RouterLink> / {id}</>}
                title={supplementary?.commercial_flight_number || supplementary?.flight_identifier || `Flight ${id}`}
                description={`${supplementary?.departure_airport || "—"} → ${supplementary?.arrival_airport || "—"}`}
                metadata={
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip label={supplementary?.aircraft_registration || "Tail unknown"} size="small" />
                    <Chip label={supplementary?.flight_identifier || "Callsign unknown"} size="small" />
                    <Chip label={signatureChipLabel} size="small" />
                    {supplementary?.leon_id ? <Chip label={`LEON ${supplementary.leon_id}`} color="success" size="small" /> : null}
                    <Chip label="All times UTC" variant="outlined" size="small" />
                  </Stack>
                }
                actions={
                  <>
                    <Button variant="contained" onClick={handleToggleJLModal} startIcon={<FlightOutlinedIcon />}>Journey log</Button>
                    {!isAcmiClient ? <Button variant="outlined" onClick={handleOpenSendOverlay} startIcon={<SendOutlinedIcon />}>Send files</Button> : null}
                    <Button variant="outlined" onClick={handlePrint} startIcon={<PrintOutlinedIcon />}>Print</Button>
                    {!isAcmiClient ? <IconButton aria-label="More flight actions" onClick={(event) => setActionsAnchor(event.currentTarget)}><MoreVertIcon /></IconButton> : null}
                  </>
                }
              />
              <Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={() => setActionsAnchor(null)}>
                <MenuItem sx={{ color: "error.main" }} onClick={() => { setActionsAnchor(null); handleOpenDeleteModal(); }}>
                  <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Delete flight…
                </MenuItem>
              </Menu>
              <FlightVerdictChips verdicts={verdicts || []} />
              <FlightAuditTrail events={auditTrail || []} />
              {!isAcmiClient&&id&&data?.journeyLog&&<FlightJourneyLog flightId={id} context={data.journeyLog} onMutated={refetch}/>}

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "370px minmax(0, 1fr)" }, gap: 2 }}>
                <Stack spacing={2} sx={{ minWidth: 0 }}>
                  <CockpitCard component="section">
                    <Typography variant="h6" sx={{ mb: 1.5 }}>Record</Typography>
                    <InfoTable
                      {...supplementary}
                      flightType={<FlightPurposeField key={id} id={id || ''}/>}
                      lastEdit={leonTransfer?.lastEdit ?? null}
                      showLeonIdRow={showLeonIdInfo}
                      leonId={supplementary?.leon_id ?? null}
                      onLeonRetransfer={showLeonIdInfo ? handleOpenLeonRetransferDialog : undefined}
                      isLeonRetransfering={leonRetransferMutation.isLoading}
                    />
                    <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 700 }}>
                      {signaturePrimaryLabel ||
                        signatureVerdict?.label ||
                        signatureSecondaryLabel ||
                        "—"}
                    </Typography>
                    {signatureSecondaryLabel && signaturePrimaryLabel ? (
                      <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
                        {signatureSecondaryLabel}
                      </Typography>
                    ) : null}
                  </CockpitCard>
                  <CockpitCard component="section"><Typography variant="h6" sx={{ mb: 1.5 }}>Timeline</Typography><TimeTable {...time} {...supplementary} delays={delay} /></CockpitCard>
                  <CockpitCard component="section"><Typography variant="h6" sx={{ mb: 1.5 }}>Fuel plan</Typography><FuelTable {...weight} {...fuel} altn={altn} fuelRows={fuelRows} weightUnit={weightUnit} /></CockpitCard>
                  {!isAcmiClient && leonTransfer?.enabled && id ? (
                    <FlightLeonTransfer flightId={id} leonTransfer={leonTransfer} onMutated={refetch} onOpenSendOverlay={handleOpenSendOverlay} />
                  ) : null}
                  <FilesList />
                </Stack>
                <Stack spacing={2} sx={{ minWidth: 0 }}>
                  <CockpitCard component="section" sx={{ p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}><Typography variant="h6">Route map</Typography><Typography variant="caption" sx={{ color: "var(--muted)" }}>Planned route · flown GPS · diversion</Typography></Stack>
                    <Map waypoints={waypoints} recorded_gps_track={recorded_gps_track ?? gpsTrack} routePoints={routePoints} onMarkerClick={handleActiveMarker} activeMarker={activeMarker} />
                  </CockpitCard>
                  <FlightFuelCheckSummaryCard summary={fuelChecksSummary || null} unit={weightUnit} onSelect={handleActiveMarker} />
                  <CockpitCard component="section" sx={{ p: 1.5 }}><FuelChecksTable fuelChecks={fuelChecks} onClick={handleActiveMarker} /></CockpitCard>
                  <CockpitCard component="section" sx={{ p: 1.5 }}><WaypointsTable waypoints={waypoints} onClick={handleActiveMarker} /></CockpitCard>
                </Stack>
              </Box>
            </Stack>
          )}
        </Page>
        <DeleteFlightModal id={deleteID} onClose={handleCloseDeleteModal} />
        <JourneyLogModal
          isOpen={isJLModalOpen}
          onClose={handleToggleJLModal}
          fuelings={fueling}
          approaches={approach}
          deicings={deicing}
          delays={delay}
          crews={crewInfo}
        />
        {!isAcmiClient && id ? (
          <FlightSendFilesOverlay
            open={isSendOverlayOpen}
            onClose={handleCloseSendOverlay}
            flightId={id}
            operatorId={supplementary?.operator_ref ?? null}
            flightIdentifier={supplementary?.flight_identifier ?? null}
            departureAirport={supplementary?.departure_airport ?? null}
            arrivalAirport={supplementary?.arrival_airport ?? null}
            leonTransfer={leonTransfer ?? null}
            onSent={refetch}
          />
        ) : null}
        <Dialog
          open={isLeonRetransferDialogOpen}
          onClose={handleCloseLeonRetransferDialog}
        >
          <DialogTitle>Re-transfer Data to Leon?</DialogTitle>
          <DialogContent>
            <Typography>
              This will reset the LEON transfer marker so SmartOFP can upload this flight again.
            </Typography>
            {leonRetransferError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {leonRetransferError}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseLeonRetransferDialog}
              disabled={leonRetransferMutation.isLoading}
            >
              No
            </Button>
            <Button
              color="success"
              disabled={leonRetransferMutation.isLoading}
              onClick={handleConfirmLeonRetransfer}
              variant="contained"
            >
              {leonRetransferMutation.isLoading ? "Sending..." : "Yes"}
            </Button>
          </DialogActions>
        </Dialog>
      </WidePageContainer>
    </>
  );
};

export default Flight;
